import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import Text from '../text/text';
import { CommentStatus } from '../types/ui-kit.types';

export type Size = 'compact' | 'large';
export type NotchColors = 'primary' | 'secondary' | 'error' | 'variant' | 'gray' | 'purple';
export type Comment = {
    id: string;
    name: string;
    date: string;
    comment: string;
};

export interface CollapsableItemI {
    label?: string;
    children?: ReactNode;
    size: Size;
    status: CommentStatus;
    color: NotchColors;
    isCollapsed: boolean;
    highlightOnSelect?: boolean;
    className?: string;
    outlined?: boolean;
    handleClick(): void;
}

const notchColor: Record<NotchColors, string> = {
    primary: 'before:bg-primary',
    secondary: 'before:bg-secondary-500',
    error: 'before:bg-error',
    variant: 'before:bg-variant-800',
    purple: 'before:bg-[#5417EE]',
    gray: 'before:bg-gray-800',
};

const borderColor: Record<NotchColors, string> = {
    primary: 'border-primary border-2 bg-primary-50',
    secondary: 'border-secondary-500 border-2 bg-secondary-50',
    error: 'border-error border-2 bg-error-50',
    variant: 'border-variant-800 border-2 bg-variant-50',
    gray: 'border-gray-800 border-2 bg-gray-50',
    purple: 'border-[#B998FA] border-2 bg-[#5417EE0F]',
};

const CollapsableItem: FC<CollapsableItemI> = ({
    label,
    children,
    size,
    status,
    handleClick,
    className,
    color = 'primary',
    isCollapsed = false,
    outlined = false,
    highlightOnSelect = false,
}) => {
    const staticContentStatusClasses: Record<CommentStatus, string> = {
        open: twMerge('bg-white text-surface-high hover:bg-gray-50', notchColor[color]),
        resolved: twMerge('bg-gray-50 text-surface-medium hover:bg-gray-100', notchColor[color]),
    };

    const staticContentSizeClasses: Record<Size, string> = {
        compact: `pl-3.5 pr-1 py-1`,
        large: `pl-4 pr-2 h-12`,
    };
    const roundedBorders = highlightOnSelect ? 'rounded-md' : isCollapsed ? 'rounded-t-md' : 'rounded-md';
    const staticContentClasses = twMerge(
        'transition font-poppins relative flex items-center justify-between',
        "cursor-pointer before:content-[''] before:absolute before:h-8",
        'before:w-1.5 before:top-[50%]',
        'before:-translate-y-1/2 before:left-0 before:rounded-r-md',
        staticContentStatusClasses[status || 'open'],
        staticContentSizeClasses[size || 'large'],
        roundedBorders,
        outlined && 'border border-gray-50',
        highlightOnSelect && borderColor[color],
        className,
    );

    return (
        <div
            className={staticContentClasses}
            onClick={() => handleClick && handleClick()}
            data-size="large"
            data-color={color}
        >
            {label ? <Text>{label}</Text> : children ? children : null}
        </div>
    );
};

CollapsableItem.defaultProps = {};

export default CollapsableItem;
